import { Color } from './java/Color';
import {
  Graphics2D,
  GraphicsConfiguration,
  Font,
  FontMetrics,
  GradientPaint,
  RadialGradientPaint,
  RenderingHints,
  BufferedImage,
} from './java/Graphics2D';
import { Point2DFloat } from './java/Geom';
import { Game } from './Game';

export class PageRenderer {

  // -- Dark theme palette --
  static readonly BG_DARK = new Color(10, 12, 18);
  static readonly BG_MID = new Color(16, 22, 34);
  static readonly SURFACE = new Color(22, 30, 44);
  static readonly SURFACE_LIGHT = new Color(30, 40, 56);
  static readonly BORDER = new Color(40, 52, 70);
  static readonly ACCENT = new Color(78, 205, 196);
  static readonly ACCENT_DIM = new Color(78, 205, 196, 40);
  static readonly DANGER = new Color(235, 87, 87);
  static readonly WARNING = new Color(245, 195, 68);
  static readonly SUCCESS = new Color(72, 199, 142);
  static readonly TEXT = new Color(230, 234, 240);
  static readonly TEXT_SEC = new Color(140, 150, 165);
  static readonly TEXT_MUTED = new Color(80, 92, 108);

  static readonly R = 12; // standard corner radius

  // -- Fonts --
  static readonly LOGO_FONT = new Font('Arial', Font.BOLD, 92);
  static readonly TITLE_FONT = new Font('Arial', Font.BOLD, 52);
  static readonly SUBTITLE_FONT = new Font('Arial', Font.PLAIN, 24);
  static readonly BUTTON_FONT = new Font('Arial', Font.BOLD, 20);
  static readonly HEADING_FONT = new Font('Arial', Font.BOLD, 24);
  static readonly BODY_FONT = new Font('Arial', Font.PLAIN, 17);
  static readonly SMALL_FONT = new Font('Arial', Font.PLAIN, 14);
  static readonly LABEL_FONT = new Font('Arial', Font.BOLD, 13);

  // -- Button dimensions --
  static readonly BTN_W = 280;
  static readonly BTN_H = 50;
  static readonly BTN_SPACING = 66;

  // Back button (fixed size, dynamic x)
  static readonly BACK_Y = 24;
  static readonly BACK_W = 90;
  static readonly BACK_H = 34;

  // Dynamic positions — depend on Game.WIDTH which adapts to screen
  static btnX(): number { return Math.trunc((Game.WIDTH - PageRenderer.BTN_W) / 2); }
  static backX(): number { return Game.WIDTH - 125; }

  private static bgCache: BufferedImage | null = null;
  private static gameBgCache: BufferedImage | null = null;

  /**
   * Both backgrounds are baked at Game.WIDTH. In Java that never changed (fullscreen at
   * startup); a browser window can be resized at any time, so the engine drops the caches
   * on resize and they re-bake at the new logical width.
   */
  static invalidateCache(): void {
    PageRenderer.bgCache = null;
    PageRenderer.gameBgCache = null;
  }

  // ===== Background =====

  static drawBackground(g: Graphics2D): void {
    if (PageRenderer.bgCache === null) {
      PageRenderer.bgCache = PageRenderer.createBackground(g.getDeviceConfiguration());
    }
    g.drawImage(PageRenderer.bgCache, 0, 0, null);
  }

  static drawGameBackground(g: Graphics2D): void {
    if (PageRenderer.gameBgCache === null) {
      PageRenderer.gameBgCache = PageRenderer.createGameBackground(g.getDeviceConfiguration());
    }
    g.drawImage(PageRenderer.gameBgCache, 0, 0, null);
  }

  private static createGameBackground(gc: GraphicsConfiguration): BufferedImage {
    // Display-compatible OPAQUE image (P1-B) — the base fill covers every pixel, so no alpha
    // is needed and this format blits without a per-frame format conversion.
    const img = gc.createCompatibleImage(Game.WIDTH, Game.HEIGHT);
    const g = img.createGraphics();
    g.setRenderingHint(RenderingHints.KEY_ANTIALIASING, RenderingHints.VALUE_ANTIALIAS_ON);

    // Dark base
    g.setColor(new Color(8, 10, 16));
    g.fillRect(0, 0, Game.WIDTH, Game.HEIGHT);

    // Subtle center glow
    g.setPaint(new RadialGradientPaint(
      new Point2DFloat(Game.WIDTH / 2, Game.HEIGHT / 2), 500,
      [0, 1],
      [new Color(20, 28, 40), new Color(8, 10, 16)],
    ));
    g.fillRect(0, 0, Game.WIDTH, Game.HEIGHT);

    // Grid dots removed — drawn live with beat pulse in Game.renderBeatVisuals()

    // Subtle edge vignette
    g.setPaint(new RadialGradientPaint(
      new Point2DFloat(Game.WIDTH / 2, Game.HEIGHT / 2),
      Math.max(Game.WIDTH, Game.HEIGHT) * 0.6,
      [0, 0.7, 1],
      [new Color(0, 0, 0, 0), new Color(0, 0, 0, 0), new Color(0, 0, 0, 60)],
    ));
    g.fillRect(0, 0, Game.WIDTH, Game.HEIGHT);

    g.dispose();
    return img;
  }

  private static createBackground(gc: GraphicsConfiguration): BufferedImage {
    // Display-compatible OPAQUE image (P1-B) — fully covered by the base gradient.
    const img = gc.createCompatibleImage(Game.WIDTH, Game.HEIGHT);
    const g = img.createGraphics();
    g.setRenderingHint(RenderingHints.KEY_ANTIALIASING, RenderingHints.VALUE_ANTIALIAS_ON);

    // Base gradient — deep dark diagonal
    g.setPaint(new GradientPaint(0, 0, PageRenderer.BG_DARK, Game.WIDTH, Game.HEIGHT, PageRenderer.BG_MID));
    g.fillRect(0, 0, Game.WIDTH, Game.HEIGHT);

    const cx = Math.trunc(Game.WIDTH / 2);

    // Large subtle teal glow — center
    g.setPaint(new RadialGradientPaint(
      new Point2DFloat(cx, 340), 600,
      [0, 0.5, 1],
      [new Color(30, 80, 78, 35), new Color(20, 50, 55, 15), new Color(0, 0, 0, 0)],
    ));
    g.fillRect(0, 0, Game.WIDTH, Game.HEIGHT);

    // Small accent highlight — upper area
    g.setPaint(new RadialGradientPaint(
      new Point2DFloat(cx, 120), 350,
      [0, 1],
      [new Color(78, 205, 196, 12), new Color(78, 205, 196, 0)],
    ));
    g.fillRect(0, 0, Game.WIDTH, Game.HEIGHT);

    g.dispose();
    return img;
  }

  // ===== Logo =====

  static drawLogo(g: Graphics2D, centerY: number): void {
    g.setFont(PageRenderer.LOGO_FONT);
    const fm: FontMetrics = g.getFontMetrics();
    const base = 'Dotch';
    const dot = '.';
    const baseW = fm.stringWidth(base);
    const dotW = fm.stringWidth(dot);
    const totalW = baseW + dotW;
    const x = Math.trunc((Game.WIDTH - totalW) / 2);

    g.setColor(PageRenderer.TEXT);
    g.drawString(base, x, centerY);
    g.setColor(PageRenderer.ACCENT);
    g.drawString(dot, x + baseW, centerY);

    // Accent underline
    const lineW = 100;
    const lineX = Math.trunc((Game.WIDTH - lineW) / 2);
    g.fillRoundRect(lineX, centerY + 12, lineW, 3, 3, 3);
  }

  // ===== Title (sub-pages) =====

  static drawTitle(g: Graphics2D, title: string): void {
    g.setFont(PageRenderer.TITLE_FONT);
    const fm: FontMetrics = g.getFontMetrics();
    const w = fm.stringWidth(title);
    g.setColor(PageRenderer.TEXT);
    g.drawString(title, Math.trunc((Game.WIDTH - w) / 2), 80);

    // Subtle accent line
    const lineW = 60;
    g.setColor(PageRenderer.ACCENT);
    g.fillRoundRect(Math.trunc((Game.WIDTH - lineW) / 2), 92, lineW, 2, 2, 2);
  }

  // Hover-enhanced colors
  private static readonly ACCENT_BRIGHT = new Color(110, 225, 218);
  private static readonly SURFACE_HOVER = new Color(38, 50, 68);
  private static readonly DANGER_BRIGHT = new Color(255, 120, 120);
  private static readonly WARNING_BRIGHT = new Color(255, 220, 100);

  // ===== Buttons (all accept hover 0.0-1.0) =====

  static drawPrimaryButton(g: Graphics2D, x: number, y: number, w: number, h: number, text: string, hover: number): void {
    g.setColor(PageRenderer.lerp(PageRenderer.ACCENT, PageRenderer.ACCENT_BRIGHT, hover));
    g.fillRoundRect(x, y, w, h, PageRenderer.R, PageRenderer.R);
    g.setFont(PageRenderer.BUTTON_FONT);
    g.setColor(PageRenderer.BG_DARK);
    PageRenderer.drawCenteredString(g, text, x, y, w, h);
  }

  static drawSecondaryButton(g: Graphics2D, x: number, y: number, w: number, h: number, text: string, hover: number): void {
    g.setColor(PageRenderer.lerp(PageRenderer.SURFACE, PageRenderer.SURFACE_HOVER, hover));
    g.fillRoundRect(x, y, w, h, PageRenderer.R, PageRenderer.R);
    g.setColor(PageRenderer.lerp(PageRenderer.BORDER, PageRenderer.ACCENT, hover * 0.5));
    g.drawRoundRect(x, y, w, h, PageRenderer.R, PageRenderer.R);
    g.setFont(PageRenderer.BUTTON_FONT);
    g.setColor(PageRenderer.lerp(PageRenderer.TEXT, PageRenderer.ACCENT_BRIGHT, hover * 0.3));
    PageRenderer.drawCenteredString(g, text, x, y, w, h);
  }

  static drawDangerButton(g: Graphics2D, x: number, y: number, w: number, h: number, text: string, hover: number): void {
    g.setColor(PageRenderer.lerp(new Color(235, 87, 87, 15), new Color(235, 87, 87, 40), hover));
    g.fillRoundRect(x, y, w, h, PageRenderer.R, PageRenderer.R);
    g.setColor(PageRenderer.lerp(PageRenderer.DANGER, PageRenderer.DANGER_BRIGHT, hover));
    g.drawRoundRect(x, y, w, h, PageRenderer.R, PageRenderer.R);
    g.setFont(PageRenderer.BUTTON_FONT);
    g.setColor(PageRenderer.lerp(PageRenderer.DANGER, PageRenderer.DANGER_BRIGHT, hover));
    PageRenderer.drawCenteredString(g, text, x, y, w, h);
  }

  static drawWarningButton(g: Graphics2D, x: number, y: number, w: number, h: number, text: string, hover: number): void {
    g.setColor(PageRenderer.lerp(new Color(245, 195, 68, 15), new Color(245, 195, 68, 40), hover));
    g.fillRoundRect(x, y, w, h, PageRenderer.R, PageRenderer.R);
    g.setColor(PageRenderer.lerp(PageRenderer.WARNING, PageRenderer.WARNING_BRIGHT, hover));
    g.drawRoundRect(x, y, w, h, PageRenderer.R, PageRenderer.R);
    g.setFont(PageRenderer.BUTTON_FONT);
    g.setColor(PageRenderer.lerp(PageRenderer.WARNING, PageRenderer.WARNING_BRIGHT, hover));
    PageRenderer.drawCenteredString(g, text, x, y, w, h);
  }

  static drawBackButton(g: Graphics2D, hover: number): void {
    const bx = PageRenderer.backX();
    g.setColor(PageRenderer.lerp(PageRenderer.SURFACE, PageRenderer.SURFACE_HOVER, hover));
    g.fillRoundRect(bx, PageRenderer.BACK_Y, PageRenderer.BACK_W, PageRenderer.BACK_H, 8, 8);
    g.setColor(PageRenderer.lerp(PageRenderer.BORDER, PageRenderer.ACCENT, hover * 0.4));
    g.drawRoundRect(bx, PageRenderer.BACK_Y, PageRenderer.BACK_W, PageRenderer.BACK_H, 8, 8);
    g.setFont(PageRenderer.SMALL_FONT);
    g.setColor(PageRenderer.lerp(PageRenderer.TEXT_SEC, PageRenderer.TEXT, hover));
    PageRenderer.drawCenteredString(g, 'Back', bx, PageRenderer.BACK_Y, PageRenderer.BACK_W, PageRenderer.BACK_H);
  }

  // ===== Utility =====

  static lerp(a: Color, b: Color, t: number): Color {
    t = Math.max(0, Math.min(1, t));
    return new Color(
      Math.trunc(a.getRed() + (b.getRed() - a.getRed()) * t),
      Math.trunc(a.getGreen() + (b.getGreen() - a.getGreen()) * t),
      Math.trunc(a.getBlue() + (b.getBlue() - a.getBlue()) * t),
      Math.trunc(a.getAlpha() + (b.getAlpha() - a.getAlpha()) * t));
  }

  // ===== Panel =====

  static drawPanel(g: Graphics2D, x: number, y: number, w: number, h: number): void {
    g.setColor(PageRenderer.SURFACE);
    g.fillRoundRect(x, y, w, h, PageRenderer.R, PageRenderer.R);
    g.setColor(PageRenderer.BORDER);
    g.drawRoundRect(x, y, w, h, PageRenderer.R, PageRenderer.R);
  }

  // ===== Utility =====

  static drawCenteredString(g: Graphics2D, text: string, rx: number, ry: number, rw: number, rh: number): void {
    const fm: FontMetrics = g.getFontMetrics();
    const x = rx + Math.trunc((rw - fm.stringWidth(text)) / 2);
    const y = ry + Math.trunc((rh - fm.getHeight()) / 2) + fm.getAscent();
    g.drawString(text, x, y);
  }
}
